// ABOUTME: Shared hook for querying and toggling a node's favorite status.
// ABOUTME: Exposes a raw toggle plus toggleWithFeedback that handles success/error snackbars.

import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
    IsFavoriteDocument,
    useIsFavoriteQuery,
    useToggleFavoriteMutation,
} from '../graphql/userFavorites.generated';

interface UseFavoriteToggleOptions {
    hierarchyId: string;
    nodeId: string;
    skip?: boolean;
    /**
     * Favorite state already carried by the caller's payload (e.g. the model-selection catalog
     * marks every product server-side). When given, the query never hits the network — it only
     * reads the cache, so a toggle done in this session still wins over the stale payload value.
     */
    knownIsFavorite?: boolean;
}

export function useFavoriteToggle({
    hierarchyId,
    nodeId,
    skip = false,
    knownIsFavorite,
}: UseFavoriteToggleOptions) {
    const { formatMessage } = useIntl();

    const { data, loading } = useIsFavoriteQuery({
        variables: { hierarchyId, nodeId },
        skip: skip || !hierarchyId || !nodeId,
        fetchPolicy: knownIsFavorite === undefined ? 'cache-first' : 'cache-only',
    });

    const [toggleMutation, { loading: toggling }] = useToggleFavoriteMutation();

    const isFavorite = data?.isFavorite ?? knownIsFavorite ?? false;

    const toggle = useCallback(async () => {
        const result = await toggleMutation({
            variables: { hierarchyId, nodeId },
            update: (cache, { data: mutationData }) => {
                if (!mutationData?.toggleFavorite) return;
                cache.writeQuery({
                    query: IsFavoriteDocument,
                    variables: { hierarchyId, nodeId },
                    data: { isFavorite: mutationData.toggleFavorite.isFavorite },
                });
            },
        });
        return result.data?.toggleFavorite ?? undefined;
    }, [hierarchyId, nodeId, toggleMutation]);

    const toggleWithFeedback = useCallback(async () => {
        try {
            const result = await toggle();
            if (result) {
                SnackBarUtils.success(
                    formatMessage({
                        id: 'user-favorites.toggle-success',
                        defaultMessage: 'Favorites updated',
                    }),
                );
            }
            return result;
        } catch {
            SnackBarUtils.error(
                formatMessage({
                    id: 'generic-error',
                    defaultMessage: 'An unexpected error occurred.',
                }),
            );
            return undefined;
        }
    }, [toggle, formatMessage]);

    return { isFavorite, loading, toggling, toggle, toggleWithFeedback };
}
